import React from "react";
import { CCard } from "../../components/common/CCard";
import {
  Box,
  Button,
  Typography,
  useTheme,
} from "@mui/material";
import { concatAddress } from "./cartUtil";
import { CaretRight } from "@phosphor-icons/react";
import { useAuth } from "../../hooks/AuthContext";
import { HeaderBox } from "./DeliveryTypeTab";
import AccessTimeFilledIcon from "@mui/icons-material/AccessTimeFilled";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";

function DeliveryCard({
  primaryAddress,
  addressList,
  toggleSelecteAddressDrawer,
}) {
  const { addressDialogOpen, setAddressDialogOpen } = useAuth();

  const theme = useTheme();

  const isAddressSelected = Object.keys(primaryAddress || {}).length > 0;
  const selectedPincode = primaryAddress?.address_pincode;

  const responsiveCardStyle = {
    px: 0,
    pb: 0,
    pt: 1.5,
    display: "flex",
    flexDirection: {
      xs: "column", // mobile
      sm: "row", // small tablets
      md: "row", // tablets and above
    },
    justifyContent: {
      xs: "flex-start",
      md: "space-between",
    },
    alignItems: {
      xs: "flex-start",
      md: "center",
    },
    gap: {
      xs: 2,
      md: 0,
    },
    borderRadius: 0,
    border: "0px solid #e5e7eb",
  };

  const responsiveButtonStyle = {
    py: 1.2,
    px: 3,
    height: "42px",
    fontSize: "14px",
    fontWeight: 600,
    border: "1px solid var(--primary-red-base)",
    borderRadius: "12px",
    alignSelf: {
      xs: "flex-end",
      md: "unset",
    },
    m: "auto",
  };

  const InfoRow = ({ label, content, showArrow }) => (
    <Box
      display="flex"
      flexWrap="wrap"
      alignItems="center"
      onClick={toggleSelecteAddressDrawer(true)}
      sx={{ cursor: "pointer" }}
    >
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: { xs: "13px", sm: "14px" },
          color: "var(--neutral-700)",
          lineHeight: "156%",
        }}
      >
        {label}
      </Typography>
      &nbsp;
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: { xs: "13px", sm: "14px" },
          color: "var(--neutral-black)",
          lineHeight: "156%",
          pl: 2,
          display: "flex",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        {content}
        {showArrow && <CaretRight size={16} color="var(--primary-red-base)" />}
      </Typography>
    </Box>
  );

  const Header = () => (
    <HeaderBox>
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 600,
          color: "var(--neutral-black)",
          fontSize: "16px",
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <AccessTimeFilledIcon
          sx={{ fontSize: 20, color: "var(--primary-red-base)" }}
        />
        Delivery Destination
      </Typography>
    </HeaderBox>
  );

  const DoorStepDeliverySelection = () =>
    addressList?.length > 0 ? (
      <CCard sx={responsiveCardStyle}>
        <Box display="flex" flexDirection="column" gap={2} flex={1}>
          <InfoRow
            label="Ship to:"
            content={
              isAddressSelected
                ? concatAddress(primaryAddress)
                : "Please select an address"
            }
            showArrow={!isAddressSelected}
          />
          {isAddressSelected && selectedPincode && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  border: "1px dashed rgba(188, 128, 15, 0.55)",
                  borderRadius: "999px",
                  px: 1.5,
                  py: 0.6,
                  width: "fit-content",
                  background: "rgba(255, 247, 233, 0.8)",
                }}
              >
                <PlaceOutlinedIcon sx={{ fontSize: 16, color: "#A66D16" }} />
                <Typography
                  sx={{ fontSize: "12px", fontWeight: 700, color: "#6E5527" }}
                >
                  Pincode {selectedPincode}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  border: "1px solid rgba(29, 118, 84, 0.28)",
                  borderRadius: "10px",
                  px: 1.2,
                  py: 0.6,
                  width: "fit-content",
                  background: "rgba(230, 248, 240, 0.9)",
                }}
              >
                <TaskAltOutlinedIcon sx={{ fontSize: 16, color: "#1D7654" }} />
                <Typography
                  sx={{ fontSize: "12px", fontWeight: 600, color: "#205C45" }}
                >
                  Serviceability will be confirmed for this destination.
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
        <Button
          onClick={toggleSelecteAddressDrawer(true)}
          variant="outlined"
          sx={responsiveButtonStyle}
        >
          Edit destination
        </Button>
      </CCard>
    ) : (
      <>
        <CCard sx={responsiveCardStyle}>
          <Box display="flex" flexDirection="column" gap={1}>
            <InfoRow
              label="Add destination"
              content="Add your delivery address"
            />
          </Box>
          <Button
            onClick={() => setAddressDialogOpen(true)}
            variant="outlined"
            sx={responsiveButtonStyle}
          >
            Add destination
          </Button>
        </CCard>
        {/* <AddressDialog
          open={addressDialogOpen}
          setOpen={setAddressDialogOpen}
          addressListFetch={addressListFetch}
        /> */}
      </>
    );

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        marginBottom: theme.spacing(2),
        overflow: "hidden",
        border: "1px solid rgba(183, 124, 10, 0.24)",
        borderRadius: "16px",
        padding: "16px",
        background:
          "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(255,250,242,0.96) 100%)",
        boxShadow: "0 10px 24px rgba(173, 132, 61, 0.08)",
      }}
    >
      <Header />
      <DoorStepDeliverySelection />
    </Box>
  );
}

export default DeliveryCard;
